import { useEffect, useState } from 'react';
import { useGetIsImpersonator } from '@leagueplatform/auth-utils';
import {
  QUERY_PARAM_KEYS,
  SSO_READY_LISTENER_TIMEOUT,
  SSO_URL_NAMES,
  WEBVIEW_MODAL_QUERY_PARAM_VALUE,
} from 'common/sso-constants';
import { sendAuthTokenIfReady } from 'utils/post-message-auth-helpers';
import { logger } from 'utils/logger';
import { captureAuthedWebviewError } from 'utils/sentry-helpers';
import { getSsoUrl, getTrustedPostMessageOrigins } from 'utils/sso-url-helpers';
import { useNavigate, useSearchParams } from '@leagueplatform/routing';
import {
  LEAGUE_MODULE_NAMES,
  getModulePath,
  useConfigProperty,
} from '@leagueplatform/core';
import { isBrowserSafari } from 'utils/is-browser-safari';
import { useAuthedWebviewUrlStore } from 'store/authed-webview-link-store';

const SSO_BENEFIT_ID_MAP = {
  [SSO_URL_NAMES.AMWELL]: '4843416d77656c6c0000000000000000',
  [SSO_URL_NAMES.SPRINGHEALTH]: '4843537072696e674865616c74682d45',
} as const;

const whitelistedSsoTargets = [
  SSO_URL_NAMES.SAPPHIRE,
  SSO_URL_NAMES.AMWELL,
  SSO_URL_NAMES.SPRINGHEALTH,
  SSO_URL_NAMES.KYRUUS,
];

export const useAuthedWebviewModal = (iframeRef: HTMLIFrameElement | null) => {
  const navigate = useNavigate();
  const handleLink = useConfigProperty('core.customMethods.handleLink');

  const { url, setUrl } = useAuthedWebviewUrlStore();
  const [searchParams, setSearchParam] = useSearchParams();
  const isImpersonation = useGetIsImpersonator();

  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isClosedByUser, setIsClosedByUser] = useState<boolean>(false);
  const [initialHistoryLength, setInitialHistoryLength] = useState(0);

  // shorthand for if the webview-modal search query is in the URL
  const isModalParamInUrl =
    searchParams.get(QUERY_PARAM_KEYS.modal) ===
    WEBVIEW_MODAL_QUERY_PARAM_VALUE;

  const onError = (caughtError: Error) => {
    setError(caughtError);
    captureAuthedWebviewError(caughtError, url || '');
  };

  const closeModal = () => {
    setIsClosedByUser(true);
    setUrl(null);
    setError(null);

    // If the modal is closed by the user, we need to navigate back in history to the point where the modal was opened.
    // Sometimes navigation occurs in the iframe, which causes the parent history to change.
    // We need to calculate the difference in history length to navigate back correctly (This is a workaround).
    // We cannot track history changes in the iframe through React.
    // Go back in history by the difference between initial and what occurred in the iframe, plus 1 extra for initial modal open navigation.
    const historyDiff = initialHistoryLength - window.history.length - 1;
    navigate(historyDiff);
  };

  useEffect(() => {
    // This opens the modal by adding the modal query param if the url is set in the store.
    if (url && !isModalParamInUrl) {
      setSearchParam((params) => {
        params.set(QUERY_PARAM_KEYS.modal, WEBVIEW_MODAL_QUERY_PARAM_VALUE);
        return params;
      });
      setInitialHistoryLength(window.history.length);
    }
  }, [url, isModalParamInUrl, setSearchParam, setInitialHistoryLength]);

  useEffect(() => {
    // If the url is not set, but the modal param is in the URL,
    // we should remove the modal param from the URL.
    // This can happen when a user refreshes the page while the modal is open.
    if (!url && isModalParamInUrl && !isClosedByUser) {
      navigate(-1);
      setIsClosedByUser(false);
    }
  }, [url, isModalParamInUrl, navigate, isClosedByUser, setIsClosedByUser]);

  // Initiate postMessage listener to handle messages from the iframe
  // and send auth token if ready.
  // This will only run if the url is set and the iframeRef is available.
  useEffect(() => {
    if (url && iframeRef?.contentWindow && isModalParamInUrl) {
      //  if is impersonator, do not initiate any postMessage logic with iframe
      if (isImpersonation) return undefined;
      const targetOrigin = new URL(url).origin;
      const messageHandler = async (event: MessageEvent) => {
        logger.log('Received postmessage event', event);

        try {
          const isAuthenticated = await sendAuthTokenIfReady(
            event,
            targetOrigin,
            iframeRef.contentWindow,
          );

          if (isAuthenticated) {
            setLoading(false);
          }
        } catch (err: any) {
          setLoading(false);
          onError(err instanceof Error ? err : new Error(err));
        }

        const action = event?.data?.action;

        const navigationTarget = event?.data?.navigate_to;

        // If event is not coming from a whitelisted origin or the src origin, ignore.
        if (
          event?.origin !== targetOrigin &&
          !getTrustedPostMessageOrigins().includes(event?.origin)
        ) {
          return;
        }

        if (navigationTarget) {
          if (whitelistedSsoTargets.includes(navigationTarget)) {
            if (isBrowserSafari()) {
              /**
               * This is a workaround to fix an issue only present within Safari.
               * - Calling handleLink doesn't work in Safari.  There is a restriction where you can't call window.open
               * when an async function.  Because we listen to the postMessage event and trigger the SSO code in
               * linkHandler wrapped within the async handler, it will consider it a
               * programmatically invoked window and not a user interaction.
               * - Don't call closeModal since it will trigger the useEffect rewriting the URL and pushing
               * it after what we push into history.  Since we redirect the modal doesn't need to be programmatically closed.
               */
              const WALLET_DETAILS_ROUTE = `${getModulePath(
                LEAGUE_MODULE_NAMES.wallet,
              )}`;

              switch (navigationTarget) {
                case SSO_URL_NAMES.SPRINGHEALTH: {
                  const springHealthBenefitUrl = `${WALLET_DETAILS_ROUTE}/${
                    SSO_BENEFIT_ID_MAP[SSO_URL_NAMES.SPRINGHEALTH]
                  }`;
                  navigate(springHealthBenefitUrl);
                  break;
                }
                case SSO_URL_NAMES.AMWELL: {
                  const amwellBenefitUrl = `${WALLET_DETAILS_ROUTE}/${
                    SSO_BENEFIT_ID_MAP[SSO_URL_NAMES.AMWELL]
                  }`;
                  navigate(amwellBenefitUrl);
                  break;
                }
                case SSO_URL_NAMES.SAPPHIRE: {
                  const careDiscoveryUrl = `${getModulePath(
                    LEAGUE_MODULE_NAMES.careDiscovery,
                  )}`;
                  navigate(careDiscoveryUrl);
                  break;
                }
                case SSO_URL_NAMES.KYRUUS: {
                  const careDiscoveryUrl = `${getModulePath(
                    LEAGUE_MODULE_NAMES.careDiscovery,
                  )}`;
                  navigate(careDiscoveryUrl);
                  break;
                }
                default: {
                  handleLink(getSsoUrl(navigationTarget));
                  closeModal();
                }
              }
            } else {
              handleLink(getSsoUrl(navigationTarget));
              closeModal();
            }
          } else {
            captureAuthedWebviewError(
              new Error(
                `Unknown navigation target received: ${navigationTarget}: ${JSON.stringify(
                  event.data,
                )}`,
              ),
              url,
            );
          }
        }

        if (action) {
          switch (action) {
            case 'close':
              closeModal();
              break;

            default:
              captureAuthedWebviewError(
                new Error(
                  `Unknown action received: ${action}: ${JSON.stringify(
                    event.data,
                  )}`,
                ),
                url,
              );
          }
        }
      };

      window.addEventListener('message', messageHandler);

      const timeout = setTimeout(() => {
        // If isLoading is still true, this means ready message wasn't received.
        // Then, set loading to false and set error state
        setLoading((loading) => {
          if (loading) {
            onError(
              new Error(
                'Could not send auth token, ready message not received from iframe.',
              ),
            );
          }
          return false;
        });
      }, SSO_READY_LISTENER_TIMEOUT);

      return () => {
        clearTimeout(timeout);
        window.removeEventListener('message', messageHandler);
      };
    }
    setLoading(true);
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iframeRef, isModalParamInUrl, url, isClosedByUser]);

  return {
    isOpen: Boolean(isModalParamInUrl && url),
    url,
    closeModal,
    isLoading,
    error,
  };
};
